'use client';

import { cn } from '@/lib/utils';
import { Check, Copy, RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

const CHAR_SETS = {
  uppercase: 'ABCDEFGHJKLMNPQRSTUVWXYZ',
  lowercase: 'abcdefghijkmnpqrstuvwxyz',
  numbers: '23456789',
  symbols: '!@#$%^&*()-_=+[]{}',
};

function generatePassword(length: number, options: Record<keyof typeof CHAR_SETS, boolean>) {
  const pools = (Object.keys(CHAR_SETS) as (keyof typeof CHAR_SETS)[]).filter((k) => options[k]);
  if (pools.length === 0) return '';
  const alphabet = pools.map((k) => CHAR_SETS[k]).join('');
  const bytes = new Uint32Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (n) => alphabet[n % alphabet.length]).join('');
}

function strengthLabel(length: number, poolCount: number) {
  const score = length * poolCount;
  if (score < 40) return { label: 'Weak', className: 'text-destructive' };
  if (score < 70) return { label: 'Okay', className: 'text-amber-600 dark:text-amber-400' };
  if (score < 100) return { label: 'Strong', className: 'text-primary' };
  return { label: 'Very strong', className: 'text-primary' };
}

export function PasswordGenerator() {
  const [length, setLength] = useState(16);
  const [options, setOptions] = useState({
    uppercase: true,
    lowercase: true,
    numbers: true,
    symbols: true,
  });
  // Empty on first render (server + client must match); the real password is
  // generated client-side only, after mount, to avoid a hydration mismatch
  // (crypto.getRandomValues would otherwise differ between server and client).
  const [password, setPassword] = useState('');
  const [copied, setCopied] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally mount-only, using the initial options value
  useEffect(() => {
    setPassword(generatePassword(16, options));
  }, []);

  const poolCount = Object.values(options).filter(Boolean).length;
  const strength = useMemo(() => strengthLabel(length, poolCount), [length, poolCount]);

  function regenerate(nextLength = length, nextOptions = options) {
    setPassword(generatePassword(nextLength, nextOptions));
  }

  function toggle(key: keyof typeof CHAR_SETS) {
    const next = { ...options, [key]: !options[key] };
    if (Object.values(next).some(Boolean)) {
      setOptions(next);
      regenerate(length, next);
    }
  }

  function copy() {
    if (!password) return;
    navigator.clipboard?.writeText(password).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center gap-2 rounded-xl border border-input bg-background px-3 py-3">
        <code className="flex-1 truncate font-mono text-lg text-foreground">{password}</code>
        <button
          type="button"
          onClick={copy}
          aria-label="Copy password"
          className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          {copied ? <Check className="size-4 text-primary" /> : <Copy className="size-4" />}
        </button>
        <button
          type="button"
          onClick={() => regenerate()}
          aria-label="Regenerate password"
          className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <RefreshCw className="size-4" />
        </button>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-foreground">Length: {length}</span>
          <span className={cn('font-medium', strength.className)}>{strength.label}</span>
        </div>
        <input
          type="range"
          name="password-length"
          min={8}
          max={32}
          value={length}
          onChange={(e) => {
            const next = Number(e.target.value);
            setLength(next);
            regenerate(next, options);
          }}
          className="mt-2 w-full accent-primary"
        />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        {(
          [
            { key: 'uppercase', label: 'Uppercase (A-Z)' },
            { key: 'lowercase', label: 'Lowercase (a-z)' },
            { key: 'numbers', label: 'Numbers (0-9)' },
            { key: 'symbols', label: 'Symbols (!@#)' },
          ] as { key: keyof typeof CHAR_SETS; label: string }[]
        ).map((opt) => (
          <label key={opt.key} className="flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              name={`password-option-${opt.key}`}
              checked={options[opt.key]}
              onChange={() => toggle(opt.key)}
              className="size-4 rounded border-input accent-primary"
            />
            {opt.label}
          </label>
        ))}
      </div>
    </div>
  );
}
